import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { GLOBAL_CONFIRMATION_KEY } from '@app/core/constants';
import { ICoreAuthorizationDTO, IDocumentResponseDTO, MoveDocument } from '@app/core/model';
import { AuthenticationService, NotificationService } from '@app/core/services';
import { TableColumn } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DocumentsContextService, DocumentsMoveContextService } from '../../core/services/context/factory';
import { DocumentMoveAuthTableConfig } from '../models/document-move-auth-table-config.model';
import { ICoreMemberDTO } from '@app/core/model';
import { PlatformService } from '@app/core/services/context/platform.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'mnc-documents-documents-move',
    templateUrl: './documents-move.component.html',
    styleUrls: ['./documents-move.component.scss'],
    standalone: false
})
export class DocumentsMoveComponent implements OnInit {
  @Input() activeAuthorization!: ICoreAuthorizationDTO;
  @Input() tenantId: string;
  @Input() documentsListFilterable = false;
  @Input() documentsListPreviewColumnFilter = false;
  @Input() memberDto: ICoreMemberDTO = null;
  
  documentFilter = (doc: IDocumentResponseDTO) => this.documentsMoveContextService.filterDocuments([doc]).length > 0;

  @Output() movedDocuments: EventEmitter<void> = new EventEmitter<void>();
  @Output() deletedDocument: EventEmitter<void> = new EventEmitter<void>();

  authReceiver: ICoreAuthorizationDTO = null;
  authReceiverTableData: DocumentMoveAuthTableConfig[] = [];
  isLoading = false;
  newAuthFiles: IDocumentResponseDTO[] = [];
  selectedFiles: IDocumentResponseDTO[] = [];
  receiverMemberDto: ICoreMemberDTO;

  cancelMove: BehaviorSubject<boolean> = new BehaviorSubject(false);
  columns: TableColumn[] = [];

  authorizationNumber = new FormControl('', [Validators.required, Validators.minLength(15)]);

  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly ref: ChangeDetectorRef,
    private readonly notificationService: NotificationService,
    private readonly documentsContextService: DocumentsContextService,
    private readonly documentsMoveContextService: DocumentsMoveContextService,
    private readonly contextService: PlatformService,
  ) { }

  ngOnInit(): void {
    this.columns = this.documentsMoveContextService.getAuthorizationTableConfig();
  }

  getAuthorization(): void {
    const newAuthorizationNumber = this.authorizationNumber.value;
    if (!newAuthorizationNumber) {
      return;
    }

    this.isLoading = true;

    this.documentsMoveContextService.getMoveDocumentData(this.tenantId, newAuthorizationNumber)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        }),
      )
      .subscribe({
        next: ([authRes, memberRes]) => {
          if (authRes && memberRes) {
            if (this.activeAuthorization.productId !== authRes.productId) {
              this.notificationService.showError("Documents can only be transferred to authorizations under the same line of business.");
              this.authReceiverTableData = [];
              return;
            }

            this.authReceiver = authRes;
            this.receiverMemberDto = memberRes;

            // There will always be 1 record, but since we are rendering it a table, it requires a collection.
            this.authReceiverTableData = this.documentsMoveContextService.mapAuthorizationTableData([this.authReceiver], memberRes);
          } else {
            this.notificationService.showError('Authorization not found');
            this.authReceiverTableData = [];
          }
        },
        error: (error) => {
          if (!(error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404))) {
            this.notificationService.showError(error.message);
          } else if (error instanceof HttpErrorResponse && error.status === 404) {
            this.notificationService.showError('Authorization not found');
          }
          this.authReceiverTableData = [];
        },
      });

    this.documentsContextService.getFilesMetaData(this.tenantId, this.activeAuthorization.authorizationId, this.activeAuthorization.authorizationNumber)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          if (Array.isArray(res)) {
            this.newAuthFiles = res;
          } else {
            this.newAuthFiles = [res];
          }
        }
      });
  }

  openCancelConfirmationPopup(): void {
    this.confirmationService.confirm({
      key: GLOBAL_CONFIRMATION_KEY,
      rejectButtonStyleClass: 'p-button-secondary',
      header: 'Confirmation',
      message: `Moving of the document will be canceled.  Do you wish to proceed?`,
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      accept: () => {
        this.clear();
      }
    });
  }

  clear() {
    this.cancelMove.next(true);
    this.authorizationNumber.setValue('');
    this.authReceiver = null;
    this.authReceiverTableData = []
    this.selectedFiles = [];
  }

  getDistinctDocumentTypes(file: IDocumentResponseDTO): string[] {
    const document = this.newAuthFiles.find((doc) => doc.documentFileName.localeCompare(file.documentFileName));

    if (document) {
      const distinctList: string[] = file.documentType.split(', ').reduce((acc: string[], cur: string) => {
        if (document.documentType.indexOf(cur) === -1) {
          acc.push(cur);
        }
        return acc;
      }, []);

      if (distinctList.length) {
        return distinctList;
      } else {
        return [document.documentType];
      }
    }
    return [...file.documentType.split(', ')];
  }

  private mapFiles(): MoveDocument[] {
    return this.selectedFiles.reduce((acc: MoveDocument[], cur: IDocumentResponseDTO) => {
      const data: MoveDocument = {
        ...cur,
        authorizationId: this.authReceiver.authorizationId as string,
        authorizationNumber: this.authReceiver.authorizationNumber as string,
        documentTypes: this.getDistinctDocumentTypes(cur),
        createdBy: cur.sender ? cur.sender : this.authenticationService.userEmail as string,
        description: cur.documentDescription,
        authCreatedYear: null,
        dateTimeFaxReceived: new Date(cur.dateTimeFaxReceived).toISOString() // Need to do this to maintain the correct timezone
      };
      acc.push(data);
      return acc;
    }, []);
  }

  private mapDocumentResponses(movedDocument: MoveDocument): IDocumentResponseDTO | null {
    const documentData = this.selectedFiles.find((doc) => doc.documentFileName === movedDocument.documentFileName);
    return documentData ? { ...documentData, ...movedDocument } : null;
  }

  // Fix issue with document list not reloading after move
  private moveFiles() {
    this.isLoading = true;
    const mappedFiles = this.mapFiles();
    this.documentsContextService.moveFiles(this.tenantId, this.activeAuthorization, mappedFiles)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          const selectedNames = this.selectedFiles.map(item => item.documentFileName).join(', ');
          this.selectedFiles = [];

          const successMsg = `${selectedNames} has been moved to ${this.authReceiver?.authorizationNumber}`;
          this.notificationService.showSuccess(successMsg);
          this.movedDocuments.emit();
        },
        error: () => {
          this.notificationService.showError('An Error occurred when files moving');
        },
      });
  }

  onDelete() {
    this.deletedDocument.emit();
  }

  async confirm() {
    if (this.authReceiver.authorizationId === this.activeAuthorization.authorizationId) {
      this.notificationService.showError('You are trying to move document to the same authorization');
    } else {
      const selectedNames = this.selectedFiles.map(item => `<li>${item.documentFileName}</li>`).join('');

      try {
        await this.notificationService.confirm({
          header: 'Confirmation',
          message: `
            <div class="font-semibold">Do you want to move the selected document(s) to the next Authorization: ${this.authorizationNumber.value}?</div>
            <div class="break-word"><ul>${selectedNames}</ul></div>
        `,
          acceptLabel: 'Confirm',
          rejectLabel: 'Cancel',
        });
        this.moveFiles();
      } catch (e) {}
    }
  }

}
